import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase';

interface PendingRegistration {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const PendingRegistrations: React.FC = () => {
  const [registrations, setRegistrations] = useState<PendingRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPendingRegistrations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const snapshot = await getDocs(collection(db, 'Registrations'));
        const pendingRegistrations: PendingRegistration[] = [];
        
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          
          // Check if status is pending (case-insensitive)
          const status = String(data.status || '').toLowerCase();
          if (status === 'pending') {
            // Extract name from different possible structures
            const primaryCamperName = data.registrationInfo?.primaryCamperName || '';
            const nameParts = primaryCamperName.split(' ');
            const firstName = nameParts[0] || data.firstName || '';
            const lastName = nameParts.slice(1).join(' ') || data.lastName || '';
            const email = data.registrationInfo?.email || data.email || '';
            
            pendingRegistrations.push({
              id: docSnap.id,
              firstName,
              lastName,
              email
            });
          }
        });
        
        setRegistrations(pendingRegistrations);
      } catch (err) {
        console.error('Error fetching pending registrations:', err);
        setError('Failed to load pending registrations');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPendingRegistrations();
  }, []);

  const exportToCSV = () => {
    if (registrations.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV content
    const headers = ['First Name', 'Last Name', 'Email Address'];
    const csvContent = [
      headers.join(','),
      ...registrations.map(reg => [
        `"${reg.firstName}"`,
        `"${reg.lastName}"`,
        `"${reg.email}"`
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `pending_registrations_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div>Loading pending registrations...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="pending-registrations-report">
      <div className="report-header">
        <h2>Pending Registrations</h2>
        <div className="report-actions">
          <span className="record-count">{registrations.length} pending registration(s)</span>
          <button 
            onClick={exportToCSV}
            className="export-button"
            disabled={registrations.length === 0}
          >
            Export to CSV
          </button>
        </div>
      </div>
      
      {registrations.length === 0 ? (
        <div className="no-data">No pending registrations found.</div>
      ) : (
        <table className="stats-table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email Address</th>
            </tr>
          </thead>
          <tbody>
            {registrations.map(registration => (
              <tr key={registration.id}>
                <td>{registration.firstName}</td>
                <td>{registration.lastName}</td>
                <td>{registration.email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PendingRegistrations; 